import { UsersUserGroupsGroupedDropdownInputModeType } from "#src/batteries-included-components/Dropdowns/UsersUserGroupsGroupedDropdownInput";
import {
  AssessTemplatedReportForCommentsAction,
  AssetTypeType,
  ChoiceTask,
  CreateEventAction,
  CreateFormActionType,
  DelayActionType,
  FormChoiceAction,
  FormSubmissionTaskType,
  isTriggerAssetCreate,
  isTriggerAssetUpdate,
  isTriggerCron,
  isTriggerFormSubmissionCreate,
  isTriggerOtherWorkflowCompletion,
  RunTemplatedReportAction,
  RunWorkerActionType,
  SendNotificationActionType,
  SendReportActionType,
  SystemActionType,
  UserTaskType,
  VariableChoiceAction,
  WorkflowConfigStepType,
  WorkflowPluginConfig,
  WorkflowStepType,
  WorkflowSystemActionType,
  WorkflowTriggerAssetCreate,
  WorkflowTriggerAssetUpdate,
  WorkflowTriggerDueDate,
  WorkflowTriggerFormCreate,
  WorkflowTriggerScheduled,
  WorkflowTriggerType,
  WorkflowTriggerWorkflowComplete,
  WorkflowUserTaskType,
  UpdateAttributesActionType,
} from "@validereinc/domain";
import { isEmpty, pick } from "es-toolkit/compat";
import { create } from "zustand";

export type WorkflowConfigurationBaseInformationFormValues =
  | Pick<
      WorkflowUserTaskType,
      "name" | "description" | "priority_id" | "is_dismissible"
    >
  | Pick<WorkflowSystemActionType, "name" | "description">;

export type SubmitFormFormValuesType = Pick<
  FormSubmissionTaskType,
  "form_schema_id" | "form_required" | "autocomplete" | "auto_attach"
>;

export type ChoiceFormValuesType = Pick<ChoiceTask, "choices">;

export type AssigneeFormValuesType = Pick<
  WorkflowUserTaskType,
  "assignee_user" | "assignee_group" | "assignment_filter"
> & {
  assignee?:
    | [group: UsersUserGroupsGroupedDropdownInputModeType, value: string]
    | null;
};

export type TriggerCronFormValuesType = Pick<WorkflowTriggerScheduled, "cron">;

export type TriggerDueDateFormValuesType = WorkflowTriggerDueDate;

export type TriggerWorkflowCompletionFormValuesType = Pick<
  WorkflowTriggerWorkflowComplete,
  "workflow_template_id"
>;

export type TriggerFormCreationFormValuesType = Pick<
  WorkflowTriggerFormCreate,
  "answer_filter" | "form_schema_id"
>;

export type TriggerAssetFormValuesType = Pick<
  WorkflowTriggerAssetCreate,
  "asset_filter" | "asset_type"
> &
  Partial<Pick<WorkflowTriggerAssetUpdate, "attributes">>;

export type FormChoiceFormValuesType = Pick<
  FormChoiceAction,
  "form_schema_id" | "choices"
>;

export type VariableChoiceFormValuesType = Pick<
  VariableChoiceAction,
  "choices"
>;

export type DelayFormValuesType = Pick<
  DelayActionType,
  "time_period" | "duration"
>;

export type RunDRVCFormValuesType = {
  filter: Record<string, any>;
  overwrite_all_values: boolean;
  seed_records: boolean;
  fill_year_months: boolean;

  month_mode: "specific" | "range";
  // For specific months mode
  specific_months?: string[]; // can include variables like "$START"
  // For range mode
  start_month?: string; // can be variable like "$START"
  end_month?: string; // can be variable like "$END"
};

export type RunCalculationsFormValuesType = {
  entity_type: AssetTypeType;
  reporting_group_id: string;
  month_mode: "specific" | "range";
  // For specific months mode
  specific_months?: string[]; // can include variables like "$START"
  // For range mode
  start_month?: string; // can be variable like "$START"
  end_month?: string; // can be variable like "$END"
  fill_year_months?: boolean; // only relevant for range mode
  promote_to_record?: boolean;
};

export type CreateEventFormValuesType = Pick<
  CreateEventAction,
  "event_schema_id" | "event_status"
> & { event_category_id?: string };

export type RunTemplatedReportFormValuesType = Pick<
  RunTemplatedReportAction,
  "name" | "templated_report_name" | "report_input" | "enable_new_version"
>;

export type LockReportFormValuesType = {
  templated_report_name?: string;
  input?: Record<string, string>;
  report_id?: string;
};

export type UpdateRecordStatusFormValuesType = {
  record_filter: Record<string, any>;
};

export type AssessTemplatedReportForCommentsFormValuesType = Pick<
  AssessTemplatedReportForCommentsAction,
  "report_id" | "header_name" | "styles_to_assess"
>;

export type UpdateAttributesFormValuesType = Pick<
  UpdateAttributesActionType,
  "assets"
>;

export type FormCreationFormValuesType = Pick<
  CreateFormActionType,
  "form_schema_id" | "form_status"
>;

export type SendNotificationFormValuesType = Pick<
  SendNotificationActionType,
  "message" | "title" | "users" | "groups"
>;

export type SendReportFormValuesType = Pick<
  SendReportActionType,
  "message" | "title" | "users" | "groups" | "report_filter" | "report_id"
>;

export type RunWorkerFormValuesType = Pick<
  RunWorkerActionType,
  "worker_id" | "worker_version_id" | "worker_run_input"
>;

// Empty strings are invalid cron expressions when evaluated by cronstrue.
// Declare the default as at 12:00 AM, only on Sunday (UTC)
export const DEFAULT_CRON = "0 0 ? * SUN *";

/**
 * Helper function to create form section state
 */
const createFormSectionState = () => ({
  baseInformation: undefined as
    | WorkflowConfigurationBaseInformationFormValues
    | undefined,
  baseInformationIsValid: false,
  submitForm: undefined as SubmitFormFormValuesType | undefined,
  submitFormIsValid: false,
  choice: undefined as ChoiceFormValuesType | undefined,
  choiceIsValid: false,
  assignee: undefined as AssigneeFormValuesType | undefined,
  assigneeIsValid: false,
  triggerCron: undefined as TriggerCronFormValuesType | undefined,
  triggerCronIsValid: false,
  triggerDueDate: undefined as TriggerDueDateFormValuesType | undefined,
  triggerDueDateIsValid: false,
  triggerWorkflowCompletion: undefined as
    | TriggerWorkflowCompletionFormValuesType
    | undefined,
  triggerWorkflowCompletionIsValid: false,
  triggerFormCreation: undefined as
    | TriggerFormCreationFormValuesType
    | undefined,
  triggerFormCreationIsValid: false,
  triggerAsset: undefined as TriggerAssetFormValuesType | undefined,
  triggerAssetIsValid: false,
  formChoice: undefined as FormChoiceFormValuesType | undefined,
  formChoiceIsValid: false,
  variableChoice: undefined as VariableChoiceFormValuesType | undefined,
  variableChoiceIsValid: false,
  formCreation: undefined as FormCreationFormValuesType | undefined,
  formCreationIsValid: false,
  delay: undefined as DelayFormValuesType | undefined,
  delayIsValid: false,
  runCalculations: undefined as RunCalculationsFormValuesType | undefined,
  runCalculationsIsValid: false,
  createEvent: undefined as CreateEventFormValuesType | undefined,
  createEventIsValid: false,
  runTemplatedReport: undefined as RunTemplatedReportFormValuesType | undefined,
  runTemplatedReportIsValid: false,
  runDRVC: undefined as RunDRVCFormValuesType | undefined,
  runDRVCIsValid: false,
  lockReport: undefined as LockReportFormValuesType | undefined,
  lockReportIsValid: false,
  updateRecordStatus: undefined as UpdateRecordStatusFormValuesType | undefined,
  updateRecordStatusIsValid: false,
  assessTemplatedReportForComments: undefined as
    | AssessTemplatedReportForCommentsFormValuesType
    | undefined,
  assessTemplatedReportForCommentsIsValid: false,
  updateAttributes: undefined as UpdateAttributesFormValuesType | undefined,
  updateAttributesIsValid: false,
  sendNotification: undefined as SendNotificationFormValuesType | undefined,
  sendNotificationIsValid: false,
  sendReport: undefined as SendReportFormValuesType | undefined,
  sendReportIsValid: false,
  runWorker: undefined as RunWorkerFormValuesType | undefined,
  runWorkerIsValid: false,
  input: undefined as Record<string, string> | undefined,
  output: undefined as Record<string, string> | undefined,
  plugin: undefined as WorkflowPluginConfig | undefined,
});

/**
 * Helper function to create form section setters
 */
const createFormSectionSetters = (
  set: (partial: Record<string, unknown>) => void
) => ({
  setBaseInformation: (
    values: WorkflowConfigurationBaseInformationFormValues
  ) => set({ baseInformation: values }),
  setBaseInformationIsValid: (isValid: boolean) =>
    set({ baseInformationIsValid: isValid }),
  setSubmitForm: (values: SubmitFormFormValuesType) =>
    set({ submitForm: values }),
  setSubmitFormIsValid: (isValid: boolean) =>
    set({ submitFormIsValid: isValid }),
  setChoice: (values: ChoiceFormValuesType) => set({ choice: values }),
  setChoiceIsValid: (isValid: boolean) => set({ choiceIsValid: isValid }),
  setAssignee: (values: AssigneeFormValuesType) => set({ assignee: values }),
  setAssigneeIsValid: (isValid: boolean) => set({ assigneeIsValid: isValid }),
  setTriggerCron: (values: TriggerCronFormValuesType) =>
    set({ triggerCron: values }),
  setTriggerCronIsValid: (isValid: boolean) =>
    set({ triggerCronIsValid: isValid }),
  setTriggerDueDate: (values: TriggerDueDateFormValuesType | undefined) =>
    set({ triggerDueDate: values }),
  setTriggerDueDateIsValid: (isValid: boolean) =>
    set({ triggerDueDateIsValid: isValid }),
  setTriggerWorkflowCompletion: (
    values: TriggerWorkflowCompletionFormValuesType
  ) => set({ triggerWorkflowCompletion: values }),
  setTriggerWorkflowCompletionIsValid: (isValid: boolean) =>
    set({ triggerWorkflowCompletionIsValid: isValid }),
  setTriggerFormCreation: (values: TriggerFormCreationFormValuesType) =>
    set({ triggerFormCreation: values }),
  setTriggerFormCreationIsValid: (isValid: boolean) =>
    set({ triggerFormCreationIsValid: isValid }),
  setTriggerAsset: (values: TriggerAssetFormValuesType) =>
    set({ triggerAsset: values }),
  setTriggerAssetIsValid: (isValid: boolean) =>
    set({ triggerAssetIsValid: isValid }),
  setFormChoice: (values: FormChoiceFormValuesType) =>
    set({ formChoice: values }),
  setFormChoiceIsValid: (isValid: boolean) =>
    set({ formChoiceIsValid: isValid }),
  setVariableChoice: (values: VariableChoiceFormValuesType) =>
    set({ variableChoice: values }),
  setVariableChoiceIsValid: (isValid: boolean) =>
    set({ variableChoiceIsValid: isValid }),
  setFormCreation: (values: FormCreationFormValuesType) =>
    set({ formCreation: values }),
  setFormCreationIsValid: (isValid: boolean) =>
    set({ formCreationIsValid: isValid }),
  setDelay: (values: DelayFormValuesType) => set({ delay: values }),
  setDelayIsValid: (isValid: boolean) => set({ delayIsValid: isValid }),
  setRunCalculations: (values: RunCalculationsFormValuesType) =>
    set({ runCalculations: values }),
  setRunCalculationsIsValid: (isValid: boolean) =>
    set({ runCalculationsIsValid: isValid }),
  setCreateEvent: (values: CreateEventFormValuesType) =>
    set({ createEvent: values }),
  setCreateEventIsValid: (isValid: boolean) =>
    set({ createEventIsValid: isValid }),
  setRunTemplatedReport: (values: RunTemplatedReportFormValuesType) =>
    set({ runTemplatedReport: values }),
  setRunTemplatedReportIsValid: (isValid: boolean) =>
    set({ runTemplatedReportIsValid: isValid }),
  setRunDRVC: (values: RunDRVCFormValuesType) => set({ runDRVC: values }),
  setRunDRVCIsValid: (isValid: boolean) => set({ runDRVCIsValid: isValid }),
  setLockReport: (values: LockReportFormValuesType) =>
    set({ lockReport: values }),
  setLockReportIsValid: (isValid: boolean) =>
    set({ lockReportIsValid: isValid }),
  setUpdateRecordStatus: (values: UpdateRecordStatusFormValuesType) =>
    set({ updateRecordStatus: values }),
  setUpdateRecordStatusIsValid: (isValid: boolean) =>
    set({ updateRecordStatusIsValid: isValid }),
  setAssessTemplatedReportForComments: (
    values: AssessTemplatedReportForCommentsFormValuesType
  ) => set({ assessTemplatedReportForComments: values }),
  setAssessTemplatedReportForCommentsIsValid: (isValid: boolean) =>
    set({ assessTemplatedReportForCommentsIsValid: isValid }),
  setUpdateAttributes: (values: UpdateAttributesFormValuesType) =>
    set({ updateAttributes: values }),
  setUpdateAttributesIsValid: (isValid: boolean) =>
    set({ updateAttributesIsValid: isValid }),
  setSendNotification: (values: SendNotificationFormValuesType) =>
    set({ sendNotification: values }),
  setSendNotificationIsValid: (isValid: boolean) =>
    set({ sendNotificationIsValid: isValid }),
  setSendReport: (values: SendReportFormValuesType) =>
    set({ sendReport: values }),
  setSendReportIsValid: (isValid: boolean) =>
    set({ sendReportIsValid: isValid }),
  setRunWorker: (values: RunWorkerFormValuesType) => set({ runWorker: values }),
  setRunWorkerIsValid: (isValid: boolean) => set({ runWorkerIsValid: isValid }),
  setInput: (values: Record<string, string>) => set({ input: values }),
  setOutput: (values: Record<string, string>) => set({ output: values }),
  setPlugin: (values: WorkflowPluginConfig) => set({ plugin: values }),

  /**
   * Initialize all form values when the drawer opens
   */
  initializeFormValues: (
    stepDefinition?: WorkflowConfigStepType,
    triggerDefinition?: WorkflowTriggerType
  ) => {
    const updates: Partial<ReturnType<typeof createFormSectionState>> = {};

    //TODO: init parallel tasks

    // Initialize user task form values
    if (stepDefinition?.type === WorkflowStepType.USER_TASK) {
      // Base Information
      const baseInformation = {
        ...pick(stepDefinition, ["name", "description", "is_dismissible"]),
        priority_id: String(stepDefinition.priority_id ?? 0),
      };
      updates.baseInformation = baseInformation;
      updates.baseInformationIsValid = Boolean(baseInformation.name);

      // Assignee
      const assigneeValues: AssigneeFormValuesType = {
        assignee_user: stepDefinition.assignee_user ?? null,
        assignee_group: stepDefinition.assignee_group ?? null,
        assignment_filter: stepDefinition.assignment_filter ?? null,
        assignee: stepDefinition.assignee_user
          ? [
              "users" as UsersUserGroupsGroupedDropdownInputModeType,
              stepDefinition.assignee_user,
            ]
          : stepDefinition.assignee_group
            ? [
                "groups" as UsersUserGroupsGroupedDropdownInputModeType,
                stepDefinition.assignee_group,
              ]
            : null,
      };
      updates.assignee = assigneeValues;
      updates.assigneeIsValid = Boolean(
        !!assigneeValues.assignee_user ||
          !!assigneeValues.assignee_group ||
          !isEmpty(assigneeValues.assignment_filter)
      );

      // Task-specific values
      if (stepDefinition.task.type === UserTaskType.SUBMIT_FORM) {
        const submitFormValues = pick(stepDefinition.task, [
          "form_schema_id",
          "autocomplete",
          "auto_attach",
          "form_required",
        ]);
        updates.submitForm = submitFormValues;
        updates.submitFormIsValid = Boolean(submitFormValues.form_schema_id);

        // Input/Output variables
        updates.input = stepDefinition.task.input ?? {};
        updates.output = stepDefinition.task.output ?? {};
      }

      if (stepDefinition.task.type === UserTaskType.CHOICE) {
        const choiceValues = pick(stepDefinition.task, ["choices"]);
        updates.choice = choiceValues;
        updates.choiceIsValid = Boolean(choiceValues.choices?.length);
      }
    }

    // Initialize trigger form values
    if (triggerDefinition) {
      // Due Date (common to all triggers)
      updates.triggerDueDate = triggerDefinition.due_date;
      updates.triggerDueDateIsValid = true; // Due date is optional

      // Trigger-specific values
      if (isTriggerCron(triggerDefinition)) {
        const cronValues = pick(triggerDefinition, ["cron"]);
        updates.triggerCron = cronValues;
        updates.triggerCronIsValid = Boolean(cronValues.cron);
      }

      if (isTriggerOtherWorkflowCompletion(triggerDefinition)) {
        const workflowCompletionValues = pick(triggerDefinition, [
          "workflow_template_id",
        ]);
        updates.triggerWorkflowCompletion = workflowCompletionValues;
        updates.triggerWorkflowCompletionIsValid = Boolean(
          workflowCompletionValues.workflow_template_id
        );
      }

      if (isTriggerFormSubmissionCreate(triggerDefinition)) {
        const formCreationValues = pick(triggerDefinition, [
          "answer_filter",
          "form_schema_id",
        ]);
        updates.triggerFormCreation = formCreationValues;
        updates.triggerFormCreationIsValid = Boolean(
          formCreationValues.form_schema_id
        );
      }

      if (
        isTriggerAssetCreate(triggerDefinition) ||
        isTriggerAssetUpdate(triggerDefinition)
      ) {
        const assetValues = pick(triggerDefinition, [
          "asset_filter",
          "asset_type",
        ]);
        if (isTriggerAssetUpdate(triggerDefinition)) {
          Object.assign(assetValues, pick(triggerDefinition, ["attributes"]));
        }
        updates.triggerAsset = assetValues;
        updates.triggerAssetIsValid = Boolean(assetValues.asset_type);
      }
    }

    if (stepDefinition?.type === WorkflowStepType.SYSTEM_ACTION) {
      const baseInformation = pick(stepDefinition, ["name", "description"]);
      updates.baseInformation = baseInformation;
      updates.baseInformationIsValid = Boolean(stepDefinition.name);

      if (stepDefinition.task.type === SystemActionType.FORM_CHOICE) {
        const formChoiceValues = pick(stepDefinition.task, [
          "form_schema_id",
          "choices",
        ]);
        updates.formChoice = formChoiceValues;
      }

      if (stepDefinition.task.type === SystemActionType.VARIABLE_CHOICE) {
        const variableChoiceValues = pick(stepDefinition.task, ["choices"]);
        updates.variableChoice = variableChoiceValues;
        updates.variableChoiceIsValid = Boolean(
          variableChoiceValues.choices &&
            variableChoiceValues.choices.length > 1 &&
            variableChoiceValues.choices.every(
              (choice) => Object.keys(choice).length > 0
            )
        );
      }

      if (stepDefinition.task.type === SystemActionType.CREATE_FORM) {
        const formCreationValues = pick(stepDefinition.task, [
          "form_schema_id",
          "form_status",
        ]);
        updates.formCreation = formCreationValues;
        updates.formCreationIsValid = Boolean(
          formCreationValues.form_schema_id && formCreationValues.form_status
        );
      }

      if (stepDefinition.task.type === SystemActionType.DELAY) {
        const delayValues = pick(stepDefinition.task, [
          "time_period",
          "duration",
        ]);

        updates.delay = delayValues;
        updates.delayIsValid =
          Number(delayValues.duration) > 0 &&
          Boolean(delayValues.time_period) &&
          Boolean(delayValues.duration);
      }

      // Not all system actions have input and output values:
      if (stepDefinition.task.input) updates.input = stepDefinition.task.input;

      if (stepDefinition.task.output)
        updates.output = stepDefinition.task.output;

      if (
        stepDefinition.task.type === SystemActionType.LOCK_RECORD ||
        stepDefinition.task.type === SystemActionType.UNLOCK_RECORD
      ) {
        const recordFilter = pick(stepDefinition.task, ["record_filter"]);

        updates.updateRecordStatus = recordFilter;
        updates.updateRecordStatusIsValid = !isEmpty(
          recordFilter.record_filter
        );
      }

      if (
        stepDefinition.task.type ===
        SystemActionType.ASSESS_TEMPLATED_REPORT_FOR_COMMENTS
      ) {
        const assessTemplatedReportForCommentsValues = pick(
          stepDefinition.task,
          ["report_id", "header_name", "styles_to_assess"]
        );

        updates.assessTemplatedReportForComments =
          assessTemplatedReportForCommentsValues;
        updates.assessTemplatedReportForCommentsIsValid =
          Boolean(assessTemplatedReportForCommentsValues.report_id) &&
          Boolean(assessTemplatedReportForCommentsValues.header_name) &&
          assessTemplatedReportForCommentsValues.styles_to_assess.length > 0;
      }

      if (
        stepDefinition.task.type ===
        SystemActionType.RUN_DEFAULT_RECORD_VALUE_CONFIGURATION
      ) {
        const { filter: rawFilter, overwrite_all_values } = stepDefinition.task;
        let { fill_year_months } = stepDefinition.task;
        const newFilter = { ...rawFilter };

        //shim for older filters, not sure if it's necessary
        if (
          Array.isArray(rawFilter.$and) &&
          rawFilter.$and.length === 2 &&
          rawFilter.$and.every((item: Record<string, any>) => item.year_month)
        ) {
          const startMonth = rawFilter.$and.find(
            (item: Record<string, any>) => item.year_month?.$gte
          );
          const endMonth = rawFilter.$and.find(
            (item: Record<string, any>) => item.year_month?.$lte
          );
          if (startMonth && endMonth) {
            newFilter.year_month = {
              $in: [startMonth.year_month.$gte, endMonth.year_month.$lte],
            };
            fill_year_months = true;
            delete newFilter.$and;
          }
        } else if (
          rawFilter.year_month &&
          typeof rawFilter.year_month === "string"
        ) {
          newFilter.year_month = { $in: [rawFilter.year_month] };
          fill_year_months = false;
        } else if (rawFilter.year_month?.$eq) {
          newFilter.year_month = { $in: [rawFilter.year_month.$eq] };
          fill_year_months = false;
        } else if (
          Array.isArray(rawFilter.year_month?.$lte) &&
          Array.isArray(rawFilter.year_month.$gte) &&
          rawFilter.year_month.$gte?.length > 0 &&
          rawFilter.year_month.$lte?.length > 0
        ) {
          newFilter.year_month = {
            $in: [
              rawFilter.year_month.$gte?.[0],
              rawFilter.year_month.$lte?.[0],
            ],
          };
          fill_year_months = true;
        }

        // Handle year_month transformation: {year_month: {$in: []}} -> year_months
        const { year_month, ...filter } = newFilter;
        let yearMonths: string[] = year_month?.$in || [];
        const isRange = fill_year_months === true;
        // Clean up data: if fill_year_months is true and we have more than 2 months,
        // only keep the first and last (sorted) since middle values are ignored anyway
        if (isRange && yearMonths.length > 2) {
          const sortedMonths = [...yearMonths].sort();
          yearMonths = [sortedMonths[0], sortedMonths[sortedMonths.length - 1]];
        }

        const runDRVCValues: RunDRVCFormValuesType = {
          filter,
          overwrite_all_values: overwrite_all_values || false,
          seed_records: true,
          month_mode: isRange ? "range" : "specific",
          ...(isRange
            ? {
                // For range mode, use the cleaned up start/end values
                start_month: yearMonths.length > 0 ? yearMonths[0] || "" : "",
                end_month:
                  yearMonths.length > 1
                    ? yearMonths[1] || ""
                    : yearMonths[0] || "",
                fill_year_months: true,
              }
            : {
                // For specific mode, use all months as-is
                specific_months: yearMonths,
                fill_year_months: false,
              }),
        };

        updates.runDRVC = runDRVCValues;
        updates.runDRVCIsValid =
          Boolean(runDRVCValues.filter) &&
          typeof runDRVCValues.overwrite_all_values === "boolean" &&
          typeof runDRVCValues.seed_records === "boolean" &&
          typeof runDRVCValues.fill_year_months === "boolean";
      }

      if (stepDefinition.task.type === SystemActionType.RUN_CALCULATIONS) {
        const task = stepDefinition.task;

        // Handle year_month transformation: {year_month: {$in: []}} -> year_months
        let yearMonths: string[] = task.year_months || [];
        const filter = { ...task.filter };

        if (
          filter.year_month &&
          typeof filter.year_month === "object" &&
          filter.year_month.$in
        ) {
          yearMonths = filter.year_month.$in;
          delete filter.year_month;
        }

        const isRange = task.fill_year_months === true;
        // Clean up data: if fill_year_months is true and we have more than 2 months,
        // only keep the first and last (sorted) since middle values are ignored anyway
        if (isRange && yearMonths.length > 2) {
          const sortedMonths = [...yearMonths].sort();
          yearMonths = [sortedMonths[0], sortedMonths[sortedMonths.length - 1]];
        }

        const runCalculationsValues: RunCalculationsFormValuesType = {
          entity_type: filter.entity_type || "",
          reporting_group_id: filter.reporting_group_id || "",
          month_mode: isRange ? "range" : "specific",
          ...(isRange
            ? {
                // For range mode, use the cleaned up start/end values
                start_month: yearMonths.length > 0 ? yearMonths[0] || "" : "",
                end_month:
                  yearMonths.length > 1
                    ? yearMonths[1] || ""
                    : yearMonths[0] || "",
                fill_year_months: true,
              }
            : {
                // For specific mode, use all months as-is
                specific_months: yearMonths,
              }),
          promote_to_record: task.promote_to_record || false,
        };

        updates.runCalculations = runCalculationsValues;
        updates.runCalculationsIsValid = Boolean(
          runCalculationsValues.entity_type &&
            runCalculationsValues.reporting_group_id &&
            (runCalculationsValues.month_mode === "specific"
              ? runCalculationsValues.specific_months?.length
              : runCalculationsValues.start_month &&
                runCalculationsValues.end_month)
        );
      }

      if (stepDefinition.task.type === SystemActionType.CREATE_EVENT) {
        const createEventValues = pick(stepDefinition.task, [
          "event_schema_id",
          "event_status",
        ]);

        updates.createEvent = createEventValues;
        updates.createEventIsValid =
          Boolean(createEventValues.event_schema_id) &&
          Boolean(createEventValues.event_status);
      }

      if (
        stepDefinition.task.type === SystemActionType.UPDATE_ATTRIBUTES ||
        stepDefinition.task.type === SystemActionType.UPDATE_CUSTOM_ATTRIBUTES
      ) {
        const updateAttributesValues = pick(stepDefinition.task, ["assets"]);

        updates.updateAttributes =
          updateAttributesValues as UpdateAttributesFormValuesType;
        updates.updateAttributesIsValid =
          Boolean(updateAttributesValues?.assets?.length) &&
          updateAttributesValues?.assets?.every(
            (asset) =>
              Boolean(asset.asset_type) &&
              Boolean(asset.asset_id) &&
              (Boolean(asset.custom_attributes) ||
                Boolean(asset.primary_attributes))
          );
      }

      if (
        stepDefinition.task.type === SystemActionType.LOCK_REPORT ||
        stepDefinition.task.type === SystemActionType.UNLOCK_REPORT
      ) {
        const lockReportValues = {
          templated_report_name:
            stepDefinition.task.report_filter?.templated_report_name,
          input: stepDefinition.task.report_filter?.input || {},
          report_id: stepDefinition.task.report_id,
        };

        updates.lockReport = lockReportValues;
        updates.lockReportIsValid = Boolean(
          lockReportValues.templated_report_name || lockReportValues.report_id
        );
      }

      if (stepDefinition.task.type === SystemActionType.RUN_TEMPLATED_REPORT) {
        const runTemplatedReportValues = pick(stepDefinition.task, [
          "name",
          "templated_report_name",
          "report_input",
          "enable_new_version",
        ]);
        updates.runTemplatedReport = runTemplatedReportValues;
        updates.runTemplatedReportIsValid = Boolean(
          runTemplatedReportValues.name &&
            runTemplatedReportValues.templated_report_name
        );
      }

      if (stepDefinition.task.type === SystemActionType.RUN_WORKER) {
        const runWorkerValues = pick(stepDefinition.task, [
          "worker_id",
          "worker_version_id",
          "worker_run_input",
        ]);
        updates.runWorker = {
          ...runWorkerValues,
          worker_version_id: runWorkerValues.worker_version_id
            ? String(runWorkerValues.worker_version_id)
            : undefined,
          worker_run_input: runWorkerValues.worker_run_input ?? {},
        } as unknown as RunWorkerFormValuesType;
        updates.runWorkerIsValid = Boolean(runWorkerValues.worker_id);
      }

      if (stepDefinition.task.type === SystemActionType.SEND_NOTIFICATION) {
        const sendNotificationValues = pick(stepDefinition.task, [
          "message",
          "title",
          "users",
          "groups",
        ]);
        updates.sendNotification = sendNotificationValues;
        updates.sendNotificationIsValid = Boolean(
          sendNotificationValues.message &&
            sendNotificationValues.title &&
            ((sendNotificationValues.users &&
              sendNotificationValues.users.length > 0) ||
              (sendNotificationValues.groups &&
                sendNotificationValues.groups.length > 0))
        );
      }

      if (stepDefinition.task.type === SystemActionType.SEND_REPORT) {
        const sendReportValues = pick(stepDefinition.task, [
          "message",
          "title",
          "users",
          "groups",
          "report_filter",
          "report_id",
        ]);
        updates.sendReport = sendReportValues;
        updates.sendReportIsValid = Boolean(
          sendReportValues.message &&
            sendReportValues.title &&
            ((sendReportValues.users && sendReportValues.users.length > 0) ||
              (sendReportValues.groups &&
                sendReportValues.groups.length > 0)) &&
            ((sendReportValues.report_filter &&
              Object.keys(sendReportValues.report_filter).length > 0) ||
              sendReportValues.report_id)
        );
      }
    }
    if (stepDefinition && "task" in stepDefinition && stepDefinition.task) {
      updates.plugin = pick(stepDefinition.task, [
        "plugin_name",
        "plugin_version",
      ]) as WorkflowPluginConfig;
    }

    // Apply all updates at once
    set(updates);
  },

  /**
   * Reset all form values and validation states
   */
  resetFormValues: () => {
    set({
      ...createFormSectionState(),
    });
  },
});

/**
 * Helper function to create form section getters
 */
const createFormSectionGetters = (get: typeof createFormSectionState) => ({
  getBaseInformation: (
    isStepSystemAction: boolean
  ): WorkflowConfigurationBaseInformationFormValues | undefined => {
    const values = get().baseInformation;
    return values
      ? {
          name: values.name ?? "",
          description: values.description ?? "",
          ...(!isStepSystemAction && {
            priority_id: (values as WorkflowUserTaskType).priority_id ?? "0",
            is_dismissible:
              (values as WorkflowUserTaskType).is_dismissible ?? false,
          }),
        }
      : undefined;
  },
  getBaseInformationIsValid: () => get().baseInformationIsValid,
  getSubmitForm: () => get().submitForm,
  getSubmitFormIsValid: () => get().submitFormIsValid,
  getChoice: () => get().choice,
  getChoiceIsValid: () => get().choiceIsValid,
  getAssignee: () => get().assignee,
  getAssigneeIsValid: () => get().assigneeIsValid,
  getTriggerCron: () => get().triggerCron,
  getTriggerCronIsValid: () => get().triggerCronIsValid,
  getTriggerDueDate: () => get().triggerDueDate,
  getTriggerDueDateIsValid: () => get().triggerDueDateIsValid,
  getTriggerWorkflowCompletion: () => get().triggerWorkflowCompletion,
  getTriggerWorkflowCompletionIsValid: () =>
    get().triggerWorkflowCompletionIsValid,
  getTriggerFormCreation: () => get().triggerFormCreation,
  getTriggerFormCreationIsValid: () => get().triggerFormCreationIsValid,
  getTriggerAsset: () => get().triggerAsset,
  getTriggerAssetIsValid: () => get().triggerAssetIsValid,
  getFormChoice: () => get().formChoice,
  getFormChoiceIsValid: () => get().formChoiceIsValid,
  getVariableChoice: () => get().variableChoice,
  getVariableChoiceIsValid: () => get().variableChoiceIsValid,
  getFormCreation: () => get().formCreation,
  getFormCreationIsValid: () => get().formCreationIsValid,
  getDelay: () => get().delay,
  getDelayIsValid: () => get().delayIsValid,
  getRunCalculations: () => get().runCalculations,
  getRunCalculationsIsValid: () => get().runCalculationsIsValid,
  getCreateEvent: () => get().createEvent,
  getCreateEventIsValid: () => get().createEventIsValid,
  getRunTemplatedReport: () => get().runTemplatedReport,
  getRunTemplatedReportIsValid: () => get().runTemplatedReportIsValid,
  getRunDRVC: () => get().runDRVC,
  getRunDRVCIsValid: () => get().runDRVCIsValid,
  getLockReport: () => get().lockReport,
  getLockReportIsValid: () => get().lockReportIsValid,
  getUpdateRecordStatus: () => get().updateRecordStatus,
  getUpdateRecordStatusIsValid: () => get().updateRecordStatusIsValid,
  getAssessTemplatedReportForComments: () =>
    get().assessTemplatedReportForComments,
  getAssessTemplatedReportForCommentsIsValid: () =>
    get().assessTemplatedReportForCommentsIsValid,
  getUpdateAttributes: () => get().updateAttributes,
  getUpdateAttributesIsValid: () => get().updateAttributesIsValid,
  getSendNotification: () => get().sendNotification,
  getSendNotificationIsValid: () => get().sendNotificationIsValid,
  getSendReport: () => get().sendReport,
  getSendReportIsValid: () => get().sendReportIsValid,
  getRunWorker: () => get().runWorker,
  getRunWorkerIsValid: () => get().runWorkerIsValid,
  getInput: () => get().input,
  getOutput: () => get().output,
  getPlugin: () => get().plugin,

  // Form-component-ready getters that provide values in the format expected by components

  getAssigneeFormValues: () => {
    const values = get().assignee;
    if (!values) return undefined;

    // Return form values that can be used directly as defaultValues
    const formValues: AssigneeFormValuesType = {};

    if (values.assignee) {
      formValues.assignee = values.assignee;
    } else if (values.assignee_user) {
      formValues.assignee = [
        "users" as UsersUserGroupsGroupedDropdownInputModeType,
        values.assignee_user,
      ];
    } else if (values.assignee_group) {
      formValues.assignee = [
        "groups" as UsersUserGroupsGroupedDropdownInputModeType,
        values.assignee_group,
      ];
    }

    return formValues;
  },

  getTriggerDueDateFormValues: () => {
    const values = get().triggerDueDate;
    if (!values) return undefined;

    return {
      ...values,
      _start_from: (values.start_variable ? "variable" : "creation") as
        | "variable"
        | "creation",
    };
  },

  getTriggerCronFormValues: () => {
    const values = get().triggerCron;
    return values ? { cron: values.cron ?? "" } : undefined;
  },

  getTriggerWorkflowCompletionFormValues: () => {
    const values = get().triggerWorkflowCompletion;
    return values
      ? { workflow_template_id: values.workflow_template_id }
      : undefined;
  },

  getTriggerFormCreationFormValues: () => {
    return get().triggerFormCreation;
  },

  getTriggerAssetFormValues: () => {
    return get().triggerAsset;
  },

  getFormChoiceFormValues: (): FormChoiceFormValuesType | undefined => {
    return get().formChoice;
  },

  getFormCreationFormValues: (): FormCreationFormValuesType | undefined => {
    return get().formCreation;
  },

  getDelayFormValues: () => {
    return get().delay;
  },

  getRunTemplatedReportFormValues: ():
    | RunTemplatedReportFormValuesType
    | undefined => {
    return get().runTemplatedReport;
  },

  getRunDRVCFormValues: (): RunDRVCFormValuesType | undefined => {
    return get().runDRVC;
  },

  getRunCalculationsFormValues: () => {
    return get().runCalculations;
  },

  getCreateEventFormValues: () => {
    return get().createEvent;
  },

  getLockReportFormValues: () => {
    return get().lockReport;
  },

  getUpdateRecordStatusFormValues: () => {
    return get().updateRecordStatus;
  },

  getAssessTemplatedReportForCommentsFormValues: () => {
    return get().assessTemplatedReportForComments;
  },

  getSendNotificationFormValues: () => {
    return get().sendNotification;
  },

  getSendReportFormValues: () => {
    return get().sendReport;
  },

  getRunWorkerFormValues: (): RunWorkerFormValuesType | undefined => {
    return get().runWorker;
  },
});

type WorkflowTemplateFormValuesStoreType = ReturnType<
  typeof createFormSectionState
> &
  ReturnType<typeof createFormSectionSetters> &
  ReturnType<typeof createFormSectionGetters>;

export const useWorkflowConfigurationFormStore =
  create<WorkflowTemplateFormValuesStoreType>((set, get) => ({
    ...createFormSectionState(),
    ...createFormSectionSetters(set),
    ...createFormSectionGetters(get),
  }));
